import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client";
import { loader } from "graphql.macro";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Error from "../components/Error";
import CurrentUser from "../components/CurrentUser";
import PleaseLogIn from "../components/PleaseLogIn";
import ManageOrganizationTable from "../components/ManageOrganizationTable/index";
import ManageOrganizationMembersTable from "../components/ManageOrganizationMembersTable/ManageOrganizationMembersTable";
import OrganizationInfo from "../components/OrganizationInfo/OrganizationInfo";
import Billing from "../components/Billing";
import LeaveOrganizationButton from "../components/LeaveOrganizationButton/LeaveOrganizationButton";
import { rolesThatCanManage } from "../lib/constants";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { faSpellCheck } from "@fortawesome/free-solid-svg-icons/faSpellCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Box,
  Button,
  Container,
  Divider,
  Fade,
  Typography,
} from "@mui/material";
import { APIKeys } from "../components/APIKeys/APIKeys";
import { Agents } from "../components/Agents/Agents";
import { Vocabularies } from "../components/Vocabularies/Vocabularies";
import {
  hasFeature,
  voiceTranslationEnabledForOrganization,
} from "../lib/candyvoice";

type OrganizationTab = "members" | "apikeys" | "agents" | "vocabularies";

const GET_ORGANIZATION_QUERY = loader(
  "../schema/query/GetOrganization.graphql"
);
const Organization = () => {
  const intl = useIntl();
  // When returning from Stripe checkout we land on /manage-organization?org=<id>,
  // so pre-select that org and show the Billing panel with its status banner.
  const orgFromUrl = new URLSearchParams(window.location.search).get("org");
  const [selectedOrganization, setSelectedOrganization] = useState(orgFromUrl);
  // Captured once: did we just return from Stripe checkout?
  const [justCompletedCheckout] = useState(
    () =>
      new URLSearchParams(window.location.search).get("checkout") === "complete"
  );
  const [selectedTab, setSelectedTab] = useState<OrganizationTab>("members");

  const { data, loading, error, stopPolling } = useQuery(
    GET_ORGANIZATION_QUERY,
    {
      variables: { id: selectedOrganization || "" },
      skip: !selectedOrganization,
      // After checkout, poll until the webhook flips the plan so the page
      // updates without a manual refresh.
      pollInterval: justCompletedCheckout ? 3000 : 0,
    }
  );

  const subscribed = !!data?.organization && data.organization.plan !== "FREE";

  // Once the plan is active, stop polling and strip the checkout params from the
  // URL (keep ?org= so the org stays selected on refresh). Otherwise cap polling.
  useEffect(() => {
    if (!justCompletedCheckout) return;
    if (subscribed) {
      stopPolling();
      const url = new URL(window.location.href);
      if (url.searchParams.has("checkout")) {
        url.searchParams.delete("checkout");
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
      }
      return;
    }
    const timeout = setTimeout(() => stopPolling(), 90000);
    return () => clearTimeout(timeout);
  }, [justCompletedCheckout, subscribed, stopPolling]);

  if (loading) return null;
  if (error) return <Error error={error} />;
  const organization = data?.organization;

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: OrganizationTab
  ) => {
    setSelectedTab(newValue);
  };

  // Select an organization and reflect it in the URL (?org=<id>) so the view is
  // refresh/share-friendly; clearing it removes the param.
  const selectOrganization = (id: string | null) => {
    setSelectedOrganization(id);
    const url = new URL(window.location.href);
    if (id) {
      url.searchParams.set("org", id);
    } else {
      url.searchParams.delete("org");
    }
    url.searchParams.delete("checkout");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <CurrentUser>
      {(currentUser) => {
        if (!currentUser) {
          return <PleaseLogIn />;
        }
        const organizations = currentUser.organizations;
        if (selectedOrganization === null && organizations.length > 0) {
          return (
            <Fade in={!loading} timeout={500}>
              <Container
                disableGutters
                sx={{
                  minWidth: "85%",
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #D1D5DB",
                    borderRadius: "12px",
                    mr: 3,
                    ml: 3,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    justifyItems="center"
                    flexWrap="wrap"
                    gap="10px 20px"
                    sx={{
                      px: 4,
                    }}
                  >
                    <Typography
                      sx={{
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        fontSize: "24px",
                        fontWeight: 600,
                        color: "#931ffa",
                      }}
                    >
                      <FormattedMessage
                        id="manage-organization.my-organizations"
                        defaultMessage="My Organizations"
                      />
                    </Typography>
                  </Box>
                  <Divider />
                  <ManageOrganizationTable
                    organizations={organizations}
                    onManageOrganization={(id: any) => selectOrganization(id)}
                    setSelectedOrganization={selectOrganization}
                  />
                  {/* <Grid container spacing={2} padding={4}></Grid> */}
                </Box>
              </Container>
            </Fade>
          );
        } else {
          const membersCount = organization.members.filter(
            (m: any) => m.status !== "REJECTED"
          )?.length;
          // the ohaio agents, not used with the CandyVoice voice translation
          const showAgents =
            organization.hasAgents &&
            !voiceTranslationEnabledForOrganization(organization);
          // the CandyVoice vocabularies, managed by the owners and the admins
          const showVocabularies =
            voiceTranslationEnabledForOrganization(organization) &&
            (organization.role === "OWNER" || organization.role === "ADMIN");

          return (
            <Container
              disableGutters
              sx={{
                minWidth: "85%",
                display: "flex",
                flex: 1,
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "12px",
                  mr: 3,
                  ml: 3,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  justifyItems="center"
                  flexWrap="wrap"
                  gap="10px 20px"
                  sx={{
                    px: 4,
                  }}
                >
                  <Typography
                    sx={{
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      fontSize: "24px",
                      fontWeight: 600,
                      color: "#931ffa",
                    }}
                  >
                    <FormattedMessage
                      id="manage.organization-info"
                      defaultMessage="Organization info"
                    />
                  </Typography>
                  {(organization.role === "OWNER" || showVocabularies) && (
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      aria-label="organization menu"
                    >
                      <Tab
                        icon={<FontAwesomeIcon icon={faUsers} />}
                        iconPosition="start"
                        label={intl.formatMessage({
                          id: "members",
                          defaultMessage: "Members",
                        })}
                        value="members"
                      />
                      {organization.role === "OWNER" && (
                        <Tab
                          icon={<FontAwesomeIcon icon={faLock} />}
                          iconPosition="start"
                          label={intl.formatMessage({
                            id: "apikeys",
                            defaultMessage: "API keys",
                          })}
                          value="apikeys"
                        />
                      )}
                      {showAgents && (
                        <Tab
                          icon={<FontAwesomeIcon icon={faRobot} />}
                          iconPosition="start"
                          label={intl.formatMessage({
                            id: "agents",
                            defaultMessage: "Agents",
                          })}
                          value="agents"
                        />
                      )}
                      {showVocabularies && (
                        <Tab
                          icon={<FontAwesomeIcon icon={faSpellCheck} />}
                          iconPosition="start"
                          label={intl.formatMessage({
                            id: "vocabularies",
                            defaultMessage: "Vocabularies",
                          })}
                          value="vocabularies"
                        />
                      )}
                    </Tabs>
                  )}
                  {organizations.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {organization.role !== "OWNER" && (
                        <LeaveOrganizationButton
                          organizationId={organization.id}
                          organizationName={organization.name}
                          onLeft={() => {
                            selectOrganization(null);
                            setSelectedTab("members");
                          }}
                        />
                      )}
                      <Button
                        onClick={() => {
                          selectOrganization(null);
                          setSelectedTab("members");
                        }}
                        variant="outlined"
                        color="secondary"
                      >
                        <FormattedMessage
                          id="actions.back-to-my-organizations"
                          defaultMessage="Back to my organizations"
                        />
                      </Button>
                    </Box>
                  )}
                </Box>
                <Divider />
                {selectedTab === "members" && (
                  <>
                    <OrganizationInfo
                      id={organization.id}
                      name={organization.name}
                      logoId={organization.logoId}
                      plan={organization.plan}
                      role={organization.role}
                      numberOfSeats={organization.numberOfSeats}
                      membersCount={membersCount}
                      options={organization.options}
                    />

                    {rolesThatCanManage.indexOf(organization.role) > -1 && (
                      <Box id="billing-plans" sx={{ m: "0 32px 40px" }}>
                        <Billing
                          organization={organization}
                          membersCount={membersCount}
                          canManage={organization.role === "OWNER"}
                        />
                      </Box>
                    )}

                    <ManageOrganizationMembersTable
                      organizationId={organization.id}
                      role={organization.role}
                      members={organization.members}
                    />
                  </>
                )}
                {selectedTab === "apikeys" && (
                  <Box
                    sx={{
                      p: 4,
                    }}
                  >
                    <APIKeys organizationId={organization.id} />
                  </Box>
                )}
                {selectedTab === "vocabularies" && showVocabularies && (
                  <Box
                    sx={{
                      p: 4,
                    }}
                  >
                    <Vocabularies
                      organizationId={organization.id}
                      customAllowed={hasFeature(
                        organization,
                        "custom_vocabulary"
                      )}
                    />
                  </Box>
                )}
                {selectedTab === "agents" && showAgents && (
                  <Box
                    sx={{
                      p: 4,
                    }}
                  >
                    <Agents organizationId={organization.id} />
                  </Box>
                )}
              </Box>
            </Container>
          );
        }
      }}
    </CurrentUser>
  );
};

export default Organization;
